import React, { useCallback, useEffect, useRef, useState } from 'react';

import SubmissionFilters, { type ISubmissionFilters } from '@components/SubmissionFilters';

import './style.scss';

export interface IMapViewFiltersDialogProps {
  isOpen: boolean;
  filters: ISubmissionFilters;
  mapCategories: ICRMCategoryResponse[];
  onApply: (filters: ISubmissionFilters) => void;
  onClose: () => void;
}

export default function MapViewFiltersDialog({
  isOpen,
  filters,
  mapCategories,
  onApply,
  onClose,
}: IMapViewFiltersDialogProps) {
  const [pendingFilters, setPendingFilters] = useState<ISubmissionFilters>(filters);

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Re-seed pending filters from the committed filters whenever the dialog opens
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  useEffect(() => {
    if (isOpen) {
      setPendingFilters(filtersRef.current);
    }
  }, [isOpen]);

  // Move focus to the close button when the dialog opens (WCAG 2.4.3)
  useEffect(() => {
    if (isOpen) {
      closeButtonRef.current?.focus();
    }
  }, [isOpen]);

  const handleApply = useCallback(() => {
    onApply(pendingFilters);
  }, [onApply, pendingFilters]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusableSelectors =
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(focusableSelectors);
        if (!focusableElements?.length) return;

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="map-filters-dialog-title"
      className="map-view-filters-dialog"
      ref={dialogRef}
      onKeyDown={handleKeyDown}
    >
      <div className="map-view-filters-dialog__header">
        <h2 id="map-filters-dialog-title" className="map-view-filters-dialog__title">
          Filters
        </h2>
        <button
          ref={closeButtonRef}
          type="button"
          aria-label="Close filters"
          className="map-view-filters-dialog__close"
          onClick={onClose}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="map-view-filters-dialog__body">
        <SubmissionFilters
          filters={pendingFilters}
          mapCategories={mapCategories}
          requestCountText=""
          onChange={setPendingFilters}
        />
      </div>
      <div className="map-view-filters-dialog__footer">
        <button type="button" className="map-view-filters-dialog__apply" onClick={handleApply}>
          Apply
        </button>
      </div>
    </div>
  );
}
